import { productService, logsService } from '@/services'
import { handleRequestError } from '@/services/apiErrorHandler'
import { mapActions } from 'vuex'
import logger from '@/logger/index'

let latestProductSearchRequest = 0

const TYPES = {
  SET_PARTS_PRICES: 'SET_PARTS_PRICES',
  SELECT_PARTS_PRODUCT: 'SELECT_PARTS_PRODUCT',
  SET_PRODUCTS: 'SET_PRODUCTS',
  SET_PRODUCT: 'SET_PRODUCT',
  SET_PRODUCT_PRICES: 'SET_PRODUCT_PRICES',
  SET_PRODUCT_STOCK: 'SET_PRODUCT_STOCK',
  SET_PRODUCT_STOCK_ARRAY: 'SET_PRODUCT_STOCK_ARRAY',
  SELECT_PRODUCT: 'SELECT_PRODUCT',
  SET_PARTS: 'SET_PARTS',
  SET_RELATED_PRODUCT: 'SET_RELATED_PRODUCT',
  SET_PRICEADD: 'SET_PRICEADD',
  RESTORE_RELATED_PRODUCT: 'RESTORE_RELATED_PRODUCT',
  SET_PARTS_PRODUCTS: 'SET_PARTS_PRODUCTS',
  SET_PARTS_BATCH_PRODUCTS: 'SET_PARTS_BATCH_PRODUCTS',
  REMOVE_SELECTED_PARTS: 'REMOVE_SELECTED_PARTS',
  ADD_SELECTED_PARTS: 'ADD_SELECTED_PARTS',
  SET_SELECTED_PARTS_PRODUCTS: 'SET_SELECTED_PARTS_PRODUCTS',
  SET_ARRAY_SELECTED_PARTS_PRODUCTS: 'SET_ARRAY_SELECTED_PARTS_PRODUCTS',
  SET_SEARCH_RESULTS_SCROLL_POSITION: 'SET_SEARCH_RESULTS_SCROLL_POSITION',
  TOGGLE_SHOW_PRICE_WITH_ADD: 'TOGGLE_SHOW_PRICE_WITH_ADD',
  RESET_SHOW_PRICE_WITH_ADD: 'RESET_SHOW_PRICE_WITH_ADD',
  SET_LAST_SEARCH_TERM: 'SET_LAST_SEARCH_TERM',
  SET_LAST_SEARCH_TERM_RAW: 'SET_LAST_SEARCH_TERM_RAW',
  SET_AGGREGATIONS: 'SET_AGGREGATIONS',
  SET_APPLIED_FILTERS: 'SET_APPLIED_FILTERS',
  SET_FILTERS_PANEL_OPEN: 'SET_FILTERS_PANEL_OPEN',
}

const state = () => ({
  items: [],
  parts: [], // conjunto de peças -- o que será mostrado na tabela
  partProducts: [], // lista de produtos do conjunto -- o que será mostrado na tabela
  selectedParts: {}, // conjunto de peças selecionado da tabela
  selectedProduct: null, // produto selecionado referente a produto-detalhes
  priceAdd: null, // preço do acréscimo do produto
  relatedProducts: [], // produtos relacionados
  selectedPartsProducts: [], // produtos selecionados do conjunto de peças
  searchResultsScrollPosition: 0, // posição do scroll da lista de resultados
  showPriceWithAdd: false, // controla exibição de preço com acréscimo
  lastSearchTerm: null, // último termo de busca para cache (normalizado/minúsculo)
  lastSearchTermRaw: null, // último termo "cru" (como o usuário digitou) para restaurar a UI
  aggregations: null, // agregações (facets) do último response /filtered
  appliedFilters: [], // filtros aplicados na última busca (espelha response.filtrosAplicados)
  filtersPanelOpen: false, // estado aberto/minimizado do painel de filtros (sobrevive à troca de rota)
})

const getters = {
  getAllProducts: (state) => {
    return state.items
  },

  getAllRelatedProducts: (state) => {
    return state.relatedProducts
  },

  getAllParts: (state) => {
    return state.parts
  },

  getPartProducts: (state) => {
    return state.partProducts
  },

  getPriceAdd: (state) => {
    return state.priceAdd
  },

  getSelectedParts: (state) => {
    return state.selectedParts
  },

  getSelectedProduct: (state) => {
    return state.selectedProduct
  },

  getBoolSelectedPartsProducts: state => {
    return state.selectedPartsProducts.length > 0
  },

  getSearchResultsScrollPosition: (state) => {
    return state.searchResultsScrollPosition
  },

  getShowPriceWithAdd: (state) => {
    return state.showPriceWithAdd
  },

  getLastSearchTerm: (state) => {
    return state.lastSearchTerm
  },

  getLastSearchTermRaw: (state) => {
    return state.lastSearchTermRaw
  },

  getAggregations: (state) => {
    return state.aggregations
  },

  getAppliedFilters: (state) => {
    return state.appliedFilters
  },

  getFiltersPanelOpen: (state) => {
    return state.filtersPanelOpen
  },
}

const actions = {
  searchProducts ({ commit, dispatch }, params) {
    const requestSequence = ++latestProductSearchRequest
    commit(TYPES.SET_PRODUCTS, [])
    commit(TYPES.SELECT_PRODUCT, null)

    // Armazena o termo de busca para cache (normalizado p/ comparação + cru p/ restaurar UI)
    const normalizedSearch = params.search ? params.search.trim().toLowerCase() : null
    if (normalizedSearch && params.type === 'descricao') {
      commit(TYPES.SET_LAST_SEARCH_TERM, normalizedSearch)
      commit(TYPES.SET_LAST_SEARCH_TERM_RAW, params.search.trim())
    }

    return new Promise((resolve, reject) => {
      productService.search(params).then((response) => {
        if (requestSequence !== latestProductSearchRequest) {
          resolve({ cancelled: true })
          return
        }

        // De-dup da API: um request mais novo cancelou este → resolve com null.
        // Sinaliza "cancelled" para o chamador ignorar e NÃO mexer no estado/UI.
        if (!response) {
          resolve({ cancelled: true })
          return
        }

        const { data } = response
        // Suporta dois shapes do api-tdr:
        // - Legado: array direto de produtos
        // - Novo: { products, aggregations, appliedFilters }
        let produtos = []
        let agregacoes = null
        let filtrosAplicados = []

        if (Array.isArray(data)) {
          produtos = data
        } else if (data && Array.isArray(data.products)) {
          produtos = data.products
          agregacoes = data.aggregations || null
          filtrosAplicados = data.appliedFilters || []
        }

        const temFiltrosAtivos = Object.keys(params).some(
          k => k !== 'search' && k !== 'type' && params[k] != null && String(params[k]).trim() !== ''
        )

        if (produtos.length) {
          commit(TYPES.SET_AGGREGATIONS, agregacoes)
          commit(TYPES.SET_APPLIED_FILTERS, filtrosAplicados)
          commit(TYPES.SET_PRODUCTS, produtos)
          dispatch('setSearchResultsScrollPosition', 0)
          resolve(produtos)
        } else if (temFiltrosAtivos) {
          // Combinação de filtros sem resultado: PRESERVA as agregações anteriores
          // (não sobrescreve com null) p/ manter painel + botão de filtro visíveis e
          // o usuário conseguir corrigir o filtro sem dar refresh.
          if (filtrosAplicados.length) commit(TYPES.SET_APPLIED_FILTERS, filtrosAplicados)
          resolve({ notFound: true })
        } else {
          // Busca crua (sem filtros) sem resultado: limpa e esconde o painel.
          commit(TYPES.SET_AGGREGATIONS, null)
          commit(TYPES.SET_APPLIED_FILTERS, [])
          resolve({ notFound: true })
        }
      })
        .catch(error => {
          if (requestSequence !== latestProductSearchRequest) {
            resolve({ cancelled: true })
            return
          }
          handleRequestError(error, reject)
        })
    })
  },

  async getProduct ({ commit, dispatch }, params) {
    commit(TYPES.SELECT_PRODUCT, null)
    return new Promise((resolve, reject) => {
      productService.get(params).then(({ data }) => {
        if (data && data.id !== 0) {
          commit(TYPES.SET_PRODUCT, data)
          // dispatch('checkProductStock', { id: data.id })
          // dispatch('setSearchResultsScrollPosition', 0)
          resolve(data)
        } else {
          resolve({ notFound: true })
        }
      })
        .catch(error => handleRequestError(error, reject))
    })
  },

  getProductDiscreet ({ commit }, params) {
    commit(TYPES.SELECT_PARTS_PRODUCT, null)
    return new Promise((resolve, reject) => {
      productService.get(params).then(({ data }) => {
        commit(TYPES.SELECT_PARTS_PRODUCT, data)
        resolve(data)
      })
        .catch(error => handleRequestError(error, reject))
    })
  },

  async getParts ({ commit }, params) {
    commit(TYPES.SET_PARTS, [])
    await productService.getParts(params).then(({ data }) => {
      commit(TYPES.SET_PARTS, data)
    }).catch(({ response }) => {
      logsService.logs({ alert: response })
    })
  },

  async getPartsProducts ({ commit, dispatch }, params) {
    try {
      commit(TYPES.SET_PARTS_PRODUCTS, [])
      const { data } = await productService.getPartsProducts(params)
      commit(TYPES.SET_PARTS_PRODUCTS, data)
      commit(TYPES.SET_SELECTED_PARTS_PRODUCTS, data)

      const codProducts = data.map(product => parseInt(product.product_id))
      await dispatch('getBatchProducts', { search: codProducts })

      return data
    } catch ({ response }) {
      logsService.logs({ alert: response })
      throw response
    }
  },

  async getBatchProducts ({ getters, commit }, params) {
    logger.debug('Iniciando busca de produtos do lote', params)

    try {
      const { data } = await productService.getBatchProducts(params)

      // logger.debug('Produtos do lote encontrados', data)
      if ((data && data.length > 0) && (getters.getSelectedParts && getters.getSelectedParts.products !== undefined)) {
        commit(TYPES.SET_PARTS_BATCH_PRODUCTS, data)
        return data
      } else {
        logger.debug('Nenhum produto encontrado para o lote', params)
        return []
      }
    } catch (error) {
      handleRequestError(error)
    }
  },

  async getRelatedProducts ({ commit }, params) {
    try {
      commit(TYPES.SET_PARTS, [])
      await productService.getRelated(params).then(({ data }) => {
        commit(TYPES.SET_PARTS, data)
        commit(TYPES.SET_RELATED_PRODUCT, data)
        return data
      })
    } catch ({ response }) {
      logsService.logs({ alert: response })
      throw response
    }
  },

  restoreRelatedProducts ({ commit }) {
    commit(TYPES.RESTORE_RELATED_PRODUCT)
  },

  checkPriceAdd ({ commit }) {
    return new Promise((resolve, reject) => {
      productService.getPriceAdd().then(({ data }) => {
        commit(TYPES.SET_PRICEADD, data)
        resolve(data)
      })
        .catch(error => handleRequestError(error, reject))
    })
  },

  checkProductPrice ({ commit }, params) {
    return new Promise((resolve, reject) => {
      productService.checkPrice(params).then(({ data }) => {
        commit(TYPES.SET_PRODUCT_PRICES, data)
        resolve(data)
      })
        .catch(error => handleRequestError(error, reject))
    })
  },

  getProductImage ({ commit }, productIdOrUrl) {
    return new Promise((resolve, reject) => {
      productService.getProductImage(productIdOrUrl).then((data) => {
        resolve(data)
      })
        .catch(error => handleRequestError(error, reject))
    })
  },

  checkPartsProductPrice ({ commit }, params) {
    return new Promise((resolve, reject) => {
      productService.checkPrice(params).then(({ data }) => {
        if (data) {
          let product = {
            id: params.id,
            price: data,
          }
          commit(TYPES.SET_PARTS_PRICES, product)
        }
        resolve(data)
      })
        .catch(error => handleRequestError(error, reject))
    })
  },

  checkProductStock ({ commit }, params) {
    return new Promise((resolve, reject) => {
      productService.checkStock(params).then(({ data }) => {
        commit(TYPES.SET_PRODUCT_STOCK, data)
        resolve(data)
      })
        .catch(error => handleRequestError(error, reject))
    })
  },

  async checkPartsProductStockArray ({ getters, commit }, params) {
    logger.debug('Entrou na verificação no array de estoques de vários produtos')

    try {
      const { data } = await productService.checkStockArray(params)

      // console.time()
      if (getters.getSelectedParts && getters.getSelectedParts.products !== undefined) {
        commit(TYPES.SET_PRODUCT_STOCK_ARRAY, data)
        // console.timeEnd()
        return
      }
    } catch (error) {
      handleRequestError(error)
    }
  },

  removeSelectedParts ({ commit }) {
    commit(TYPES.REMOVE_SELECTED_PARTS)
  },

  addSelectedParts ({ commit }, payload) {
    commit(TYPES.ADD_SELECTED_PARTS, payload)
  },

  selectProduct ({ commit, dispatch }, params) {
    commit(TYPES.SELECT_PRODUCT, params)
    dispatch('checkProductStock', { id: params.search })
    commit(TYPES.SET_PRODUCTS, [])
    commit(TYPES.SET_PRODUCT_PRICES, [])
  },

  updatePriceAdd ({ commit }, params) {
    return new Promise((resolve, reject) => {
      productService.updatePriceAdd(params).then(({ data }) => {
        resolve(data)
      })
        .catch(error => handleRequestError(error, reject))
    })
  },

  setSelectedPartsProductsArray ({ commit }, params) {
    commit(TYPES.SET_ARRAY_SELECTED_PARTS_PRODUCTS, params)
  },

  setSearchResultsScrollPosition ({ commit }, position) {
    commit(TYPES.SET_SEARCH_RESULTS_SCROLL_POSITION, position)
  },

  setFiltersPanelOpen ({ commit }, value) {
    commit(TYPES.SET_FILTERS_PANEL_OPEN, value)
  },

  toggleShowPriceWithAdd ({ commit }) {
    commit(TYPES.TOGGLE_SHOW_PRICE_WITH_ADD)
  },

  resetShowPriceWithAdd ({ commit }) {
    commit(TYPES.RESET_SHOW_PRICE_WITH_ADD)
  },
}

const mutations = {
  [TYPES.SET_PRODUCTS] (state, payload) {
    state.items = [...payload]
  },

  [TYPES.SELECT_PARTS_PRODUCT] (state, payload) {
    if (payload !== 0 || null) {
      const index = state.selectedParts.products.findIndex(product => product.product_id === payload?.id)
      state.selectedParts.products[index] = { ...payload, ...state.selectedParts.products[index] }
    }
  },

  [TYPES.SELECT_PRODUCT] (state, payload) {
    state.selectedProduct = payload
  },

  [TYPES.SET_PRODUCT] (state, payload) {
    state.selectedProduct = null
    if (payload.id > 0) {
      state.selectedProduct = payload
    }
  },

  [TYPES.SET_PRODUCT_PRICES] (state, payload) {
    state.selectedProduct.price = payload
  },

  [TYPES.SET_PARTS_PRICES] (state, payload) {
    state.selectedParts.products.find(product => product.product_id === payload?.id).price = payload.price
  },

  [TYPES.SET_PARTS_BATCH_PRODUCTS] (state, payload) {
    payload.forEach(item => {
      const index = state.selectedParts.products
        .findIndex(p => p.product_id === item.id)

      if (index !== -1) {
      // Mescla só as propriedades novas em cima do objeto existente
        Object.assign(state.selectedParts.products[index], item)
      }
    })
  },

  [TYPES.SET_PRODUCT_STOCK] (state, payload) {
    state.selectedProduct.stock = payload
  },

  [TYPES.SET_PRODUCT_STOCK_ARRAY] (state, payload) {
    for (const index in payload) {
      if (payload.hasOwnProperty(index)) {
        state.selectedParts.products.find(product => product.product_id === parseInt(index)).stock = payload[index]
      }
    }
  },

  [TYPES.SET_PARTS] (state, payload) {
    state.parts = payload
  },

  [TYPES.SET_PRICEADD] (state, payload) {
    state.priceAdd = payload
  },

  [TYPES.SET_RELATED_PRODUCT] (state, payload) {
    state.relatedProducts = [...payload]
  },

  [TYPES.RESTORE_RELATED_PRODUCT] (state) {
    state.parts = state.relatedProducts
  },

  [TYPES.SET_PARTS_PRODUCTS] (state, payload) {
    state.partProducts = payload
  },

  [TYPES.SET_SELECTED_PARTS_PRODUCTS] (state, payload) {
    state.selectedParts.products = payload.map(product => ({ product_id: product.product_id }))
  },

  [TYPES.ADD_SELECTED_PARTS] (state, payload) {
    state.selectedParts.id = parseInt(payload.id)
    state.selectedParts.description = payload.description
  },

  [TYPES.REMOVE_SELECTED_PARTS] (state) {
    state.selectedParts = {}
    state.partProducts = []
  },

  [TYPES.SET_ARRAY_SELECTED_PARTS_PRODUCTS] (state, payload) {
    state.selectedPartsProducts = payload
  },

  [TYPES.SET_SEARCH_RESULTS_SCROLL_POSITION] (state, payload) {
    state.searchResultsScrollPosition = payload
  },

  [TYPES.TOGGLE_SHOW_PRICE_WITH_ADD] (state) {
    state.showPriceWithAdd = !state.showPriceWithAdd
  },

  [TYPES.RESET_SHOW_PRICE_WITH_ADD] (state) {
    state.showPriceWithAdd = false
  },

  [TYPES.SET_LAST_SEARCH_TERM] (state, payload) {
    state.lastSearchTerm = payload
  },

  [TYPES.SET_LAST_SEARCH_TERM_RAW] (state, payload) {
    state.lastSearchTermRaw = payload
  },

  [TYPES.SET_AGGREGATIONS] (state, payload) {
    state.aggregations = payload
  },

  [TYPES.SET_APPLIED_FILTERS] (state, payload) {
    state.appliedFilters = Array.isArray(payload) ? [...payload] : []
  },

  [TYPES.SET_FILTERS_PANEL_OPEN] (state, payload) {
    state.filtersPanelOpen = !!payload
  },
}

export default {
  methods: {
    ...mapActions(['setHeaderBack', 'setFeedback']),
  },
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
}
